import React, { useState } from 'react';
import { Form, Input, Modal, message } from 'antd';
import SettingsLayout from '../layout/SettingsLayout';
import { useAuth } from '../../contexts/AuthContext';
import { cognitoService } from '../../services/cognito.service';

// ─── Name Edit Modal ──────────────────────────────────────────────────────────

const EditNameModal: React.FC<{
  open: boolean;
  currentName: string;
  onClose: () => void;
  onSaved: (name: string) => void;
}> = ({ open, currentName, onClose, onSaved }) => {
  const { accessToken } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleOk = async () => {
    const values = await form.validateFields();
    if (!accessToken) {
      message.error('No hay sesión activa.');
      return;
    }
    setLoading(true);
    try {
      await cognitoService.updateName(accessToken, values.name);
      message.success('Nombre actualizado. Se reflejará al reiniciar sesión.');
      onSaved(values.name);
      onClose();
    } catch (err: any) {
      message.error(err.message || 'No se pudo actualizar el nombre.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Editar nombre"
      open={open}
      onOk={handleOk}
      onCancel={onClose}
      okText="Guardar"
      cancelText="Cancelar"
      confirmLoading={loading}
      afterOpenChange={(visible) => { if (visible) form.setFieldsValue({ name: currentName }); }}
    >
      <Form form={form} layout="vertical" className="mt-4">
        <Form.Item
          name="name"
          label="Nombre completo"
          rules={[
            { required: true, message: 'Ingresa tu nombre.' },
            { min: 2, message: 'Mínimo 2 caracteres.' },
            { max: 60, message: 'Máximo 60 caracteres.' },
          ]}
        >
          <Input placeholder="Tu nombre" autoFocus />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─── Change Password Modal ────────────────────────────────────────────────────

const ChangePasswordModal: React.FC<{
  open: boolean;
  onClose: () => void;
}> = ({ open, onClose }) => {
  const { accessToken } = useAuth();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const handleOk = async () => {
    const values = await form.validateFields();
    if (!accessToken) {
      message.error('No hay sesión activa.');
      return;
    }
    setLoading(true);
    try {
      await cognitoService.changePassword(accessToken, values.currentPassword, values.newPassword);
      message.success('Contraseña actualizada correctamente.');
      form.resetFields();
      onClose();
    } catch (err: any) {
      const code = (err as any).code || '';
      if (code === 'NotAuthorizedException') {
        message.error('La contraseña actual es incorrecta.');
      } else if (code === 'InvalidPasswordException') {
        message.error('La nueva contraseña no cumple los requisitos de seguridad.');
      } else {
        message.error(err.message || 'No se pudo cambiar la contraseña.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Cambiar contraseña"
      open={open}
      onOk={handleOk}
      onCancel={() => { form.resetFields(); onClose(); }}
      okText="Actualizar"
      cancelText="Cancelar"
      confirmLoading={loading}
    >
      <Form form={form} layout="vertical" className="mt-4">
        <Form.Item
          name="currentPassword"
          label="Contraseña actual"
          rules={[{ required: true, message: 'Ingresa tu contraseña actual.' }]}
        >
          <Input.Password placeholder="Contraseña actual" />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="Nueva contraseña"
          rules={[
            { required: true, message: 'Ingresa la nueva contraseña.' },
            { min: 8, message: 'Mínimo 8 caracteres.' },
          ]}
        >
          <Input.Password placeholder="Nueva contraseña" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirmar nueva contraseña"
          dependencies={['newPassword']}
          rules={[
            { required: true, message: 'Confirma la nueva contraseña.' },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('newPassword') === value) return Promise.resolve();
                return Promise.reject(new Error('Las contraseñas no coinciden.'));
              },
            }),
          ]}
        >
          <Input.Password placeholder="Confirmar contraseña" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

// ─── Row helper ───────────────────────────────────────────────────────────────

const InfoRow: React.FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div className="flex items-center py-4 border-b border-gray-100 dark:border-gray-700 last:border-0">
    <span className="text-sm text-gray-500 dark:text-gray-400 w-64 flex-shrink-0">{label}</span>
    <span className={`text-sm font-medium text-gray-900 dark:text-white ${mono ? 'font-mono text-xs' : ''}`}>
      {value}
    </span>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const Settings: React.FC = () => {
  const { user } = useAuth();
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [localName, setLocalName] = useState<string | null>(null);

  const displayName = localName ?? user?.name ?? '';
  const tenantShort = user?.tenantId?.replace(/^tenant-/, '') ?? '';

  return (
    <SettingsLayout>
      <div>
        {/* Header */}
        <div className="space-y-4 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-1 h-10 bg-gray-900 dark:bg-white" />
            <h1 className="text-2xl font-normal text-gray-900 dark:text-white">Perfil</h1>
          </div>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Revisa y actualiza la información de tu cuenta.
          </p>
        </div>

        {/* Avatar + name */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden mb-6">
          <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">Información personal</h2>
            <button
              onClick={() => setNameModalOpen(true)}
              className="px-6 py-1.5 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-bold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              Editar nombre
            </button>
          </div>

          <div className="px-8 py-2">
            {/* Avatar */}
            <div className="flex items-center py-6 border-b border-gray-100 dark:border-gray-700">
              <span className="text-sm text-gray-500 dark:text-gray-400 w-64 flex-shrink-0">Avatar</span>
              <div className="w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                <span className="text-white text-xl font-bold">
                  {displayName ? displayName[0].toUpperCase() : '?'}
                </span>
              </div>
            </div>

            <InfoRow label="Nombre" value={displayName || 'No disponible'} />
            <InfoRow label="Email" value={user?.email || 'No disponible'} />
            {tenantShort && <InfoRow label="ID de cuenta" value={tenantShort} mono />}
          </div>
        </div>

        {/* Security */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-lg font-bold text-gray-800 dark:text-gray-200">Seguridad</h2>
          </div>

          <div className="px-8 py-2">
            <div className="flex items-center py-4">
              <span className="text-sm text-gray-500 dark:text-gray-400 w-64 flex-shrink-0">Contraseña</span>
              <div className="flex items-center gap-6">
                <span className="text-sm font-medium text-gray-900 dark:text-white tracking-widest">••••••••</span>
                <button
                  onClick={() => setPasswordModalOpen(true)}
                  className="text-sm text-indigo-600 dark:text-indigo-400 font-medium hover:underline"
                >
                  Cambiar contraseña
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EditNameModal
        open={nameModalOpen}
        currentName={displayName}
        onClose={() => setNameModalOpen(false)}
        onSaved={setLocalName}
      />
      <ChangePasswordModal
        open={passwordModalOpen}
        onClose={() => setPasswordModalOpen(false)}
      />
    </SettingsLayout>
  );
};

export default Settings;
